import { Stack, Typography, useTheme } from "@mui/material";
import { CloseIcon, SsoXmlFileIcon, UploadIcon } from "components";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import {
  parseSamlMetadata,
  readFileAsText,
  ParsedSamlMetadata,
} from "./parse-saml-metadata";

interface XmlFileUploadProps {
  file?: File | null;
  onMetadataParsed: ({
    parsedMetadata,
    rawXmlContent,
  }: {
    parsedMetadata: ParsedSamlMetadata;
    rawXmlContent: string;
  }) => void;
}

export const XmlFileUpload = ({
  file,
  onMetadataParsed,
}: XmlFileUploadProps) => {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(file || null);

  const onDrop = async (acceptedFiles: File[]) => {
    const acceptedFile = acceptedFiles?.[0] || null;
    if (acceptedFile) {
      setSelectedFile(acceptedFile);
      if (onMetadataParsed) {
        try {
          const xmlContent = await readFileAsText(acceptedFile);
          const parsedMetadata = parseSamlMetadata(xmlContent);
          onMetadataParsed({
            parsedMetadata,
            rawXmlContent: xmlContent,
          });
        } catch (error) {
          toast.error("Failed to parse XML metadata");
        }
      }
    }
  };

  const sizeValidator = (file: File) => {
    if (file.size > 10485760) {
      toast.error("File size larger than 10MB");
      return {
        code: "size-too-large",
        message: "Size is larger than expected",
      };
    }
    return null;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: () => {
      toast.error("Invalid file type. Please upload a valid XML file");
    },
    accept: {
      "application/xml": [".xml"],
      "text/xml": [".xml"],
    },
    validator: sizeValidator,
    maxFiles: 1,
  });

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  if (selectedFile) {
    return (
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px",
          borderRadius: "4px",
          border: `1px solid ${theme.palette.neutral["015"]}`,
          bgcolor: theme.palette.neutral["005"],
        }}
      >
        <Stack direction="row" gap="6px" alignItems="center">
          <SsoXmlFileIcon />
          <Stack>
            <Typography variant="body-small" color={theme.palette.lightBg.high}>
              {selectedFile.name}
            </Typography>
            <Typography variant="body-xsmall" color={theme.palette.lightBg.low}>
              {(selectedFile.size / 1024).toFixed(1)} KB
            </Typography>
          </Stack>
        </Stack>
        <CloseIcon
          onClick={handleRemoveFile}
          style={{
            height: "24px",
            width: "24px",
          }}
        />
      </Stack>
    );
  }

  return (
    <Stack
      {...getRootProps()}
      sx={{
        height: "72px",
        bgcolor: theme.palette.secondary["purple-tint-3"],
        border: `${isDragActive ? "2px" : "1px"} dashed ${
          theme.palette.primary.main
        }`,
        borderRadius: "8px",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        flexDirection: "row",
        cursor: "pointer",
        transition: "border 0.2s ease",
      }}
    >
      <input {...getInputProps()} />
      <Stack alignItems="center">
        <UploadIcon
          fontSize="medium"
          sx={{ color: theme.palette.purple.main }}
        />
        <Typography variant={"button-small"} color={theme.palette.primary.main}>
          Upload metadata XML file
        </Typography>
      </Stack>
    </Stack>
  );
};
